/**
 * Formats a numeric value as a percentage string using the `Intl.NumberFormat` API.
 *
 * @example
 * formatPercent(0.25);            // "25%"
 * formatPercent(0.2567);          // "25.67%"
 * formatPercent(0.2567, false);   // "26%"
 */
export const formatPercent = (
  value: number,
  fraction: boolean = true,
): string => {
  const format = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: fraction ? 2 : 0,
  });

  return format.format(value);
};

/**
 * Formats a number as currency and returns its individual parts (e.g. sign, currency symbol, integer, fraction).
 * Uses `Intl.NumberFormat.formatToParts` for precise localization.
 *
 * @example
 * getCurrencyParts(1234.56, "USD")
 * // {
 * //   plusSign: undefined,
 * //   minusSign: undefined,
 * //   currency: "$",
 * //   integer: "1234",
 * //   decimal: ".",
 * //   fraction: "56"
 * // }
 *
 * @example
 * getCurrencyParts(-78.9, "EUR")
 * // {
 * //   minusSign: "-",
 * //   currency: "€",
 * //   integer: "78",
 * //   decimal: ".",
 * //   fraction: "90"
 * // }
 */
export const getCurrencyParts = (value: number, currency: string) => {
  const format = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });

  const parts = format.formatToParts(value);

  return {
    plusSign: parts.find((part) => part.type === "plusSign")?.value,
    minusSign: parts.find((part) => part.type === "minusSign")?.value,
    currency: parts.find((part) => part.type === "currency")?.value,
    integer: parts.find((part) => part.type === "integer")?.value,
    decimal: parts.find((part) => part.type === "decimal")?.value,
    fraction: parts.find((part) => part.type === "fraction")?.value,
  };
};

export const formatCurrency = (value: number, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(value);
};

interface FormatBytesOptions {
  unit?: "B" | "KB" | "MB" | "GB" | "TB" | "PB";
  showUnit?: boolean;
  zeroUnit?: FormatBytesOptions["unit"];
  decimals?: number;
}

/**
 * Formats a byte value into a human-readable string, using either automatic or specified units.
 *
 * @param bytes - The number of bytes to format.
 * @param options - Optional configuration for formatting.
 * @param options.unit - Force output in a specific unit (`"B"`, `"KB"`, `"MB"`, `"GB"`, `"TB"`, or `"PB"`).
 * @param options.showUnit - Whether to append the unit to the result (default: `true`).
 * @param options.decimals - Number of decimal places to include (default: `2`).
 *
 * @returns A formatted string representing the size, optionally with the unit.
 *
 * @example
 * formatBytes(1024); // "1KB"
 * formatBytes(123123); // "120.21KB"
 * formatBytes(123123, { unit: "MB" }); // "0.12MB"
 * formatBytes(1231234567, { unit: "GB", decimals: 3 }); // "1.147GB"
 * formatBytes(123123, { showUnit: false }); // "120.21"
 */
export const formatBytes = (bytes: number, options?: FormatBytesOptions) => {
  if (bytes === 0) return `0${options?.zeroUnit ?? ""}`;

  const k = 1024;
  const decimals = options?.decimals ?? 2;
  const sizes: FormatBytesOptions["unit"][] = [
    "B",
    "KB",
    "MB",
    "GB",
    "TB",
    "PB",
  ];

  const index =
    options?.unit !== undefined
      ? sizes.indexOf(options.unit)
      : Math.floor(Math.log(bytes) / Math.log(k));

  const value =
    options?.unit !== undefined ? bytes / k ** index : bytes / k ** index;

  const unit = options?.unit ?? sizes[index];

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  return `${formatter.format(value)}${options?.showUnit === false ? "" : unit}`;
};

interface FormatCompactOptions {
  maximumSignificantDigits?: number;
  maximumFractionDigits?: number;
}

export const formatCompact = (
  value: number,
  options?: FormatCompactOptions,
) => {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumSignificantDigits: options?.maximumSignificantDigits ?? 3,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(value);
};

export const formatDelta = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    notation: Math.abs(value) < 10000 ? undefined : "compact",
    signDisplay: "exceptZero",
    useGrouping: false,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatPretty = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    useGrouping: true,
    maximumFractionDigits: 2,
  })
    .formatToParts(value)
    .map((part) => (part.type === "group" ? "\u00A0" : part.value))
    .join("");
};
